import { useEffect, useRef, useState } from "react";

interface AnimatedStatProps {
  value: string;  // e.g. "100+", "50+", "5+", "24h"
  label: string;
  delay?: number;
}

export default function AnimatedStat({ value, label, delay = 0 }: AnimatedStatProps) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const [glowing, setGlowing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Parse numeric part and suffix
  const numMatch = value.match(/^(\d+)(.*)$/);
  const target = numMatch ? parseInt(numMatch[1]) : 0;
  const suffix = numMatch ? numMatch[2] : value;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setTimeout(() => setStarted(true), delay);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started, delay]);

  useEffect(() => {
    if (!started) return;
    const duration = 1800;
    let startTime: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(target);
        setGlowing(true);
        setTimeout(() => setGlowing(false), 600);
      }
    };
    requestAnimationFrame(animate);
  }, [started, target]);

  return (
    <div ref={ref} className="text-center">
      <div
        className="font-display transition-all duration-300"
        style={{
          fontSize: 'clamp(2rem, 4vw, 2.8rem)',
          fontFamily: "'Sora', sans-serif",
          fontWeight: 800,
          letterSpacing: '-0.03em',
          backgroundImage: glowing
            ? 'linear-gradient(135deg, #7eb8ff 0%, #4f8cff 50%, #a78bfa 100%)'
            : 'linear-gradient(135deg, #e8edf5 0%, #4f8cff 100%)',
          backgroundSize: '100%',
          backgroundRepeat: 'no-repeat',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textShadow: 'none',
          filter: glowing ? 'drop-shadow(0 0 12px rgba(79,140,255,0.5))' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        {started ? `${count}${suffix}` : `0${suffix}`}
      </div>
      <div
        className="text-xs mt-1.5 font-medium tracking-wide uppercase"
        style={{ color: '#5a7090', fontFamily: "'Inter', sans-serif", letterSpacing: '0.08em' }}
      >
        {label}
      </div>
    </div>
  );
}
